// "Process all steps and send reply" (2026-10-23, docs/agent-draft-shadow.md,
// "Keep promise and action together"). The panel runs every step that is
// ready, in order, through POST /api/ai/suggestions/:id/process-steps (one
// pass, stopping at the first failure), and sends the reply only when every
// step succeeded. Pure helpers, tested in ui/test/processAll.test.js.

import { isCustomerFacingSendAction, isProcessableStep, isManualStep, stepPromiseKinds, findPromisedActions, LABEL_LINK_TOKEN, hasPendingLabelLinkSlot, labelLinkFillStepIndex, labelLinkSlotUrls, fillLabelLinkSlot, replyAfterEbayAttach, withoutEbayPrintLine, stepBlockState, STEP_NOT_NEEDED_NOTE } from '@cxhub/shared'
import { checkIsStale } from './returnStepCheck.js'
import { rmaReferenceStepIndex, hasRmaReferenceToken } from '@cxhub/shared'
import { isActionInputSatisfied, replacementLabelSiblingIndex, resendLabelCreateSiblingIndex, automatedReturnNoticeLabelSiblingIndex, issueEbayReturnRefundNoteSiblingIndex, stepProcessGate } from './suggestionActionInputs.js'
import { replacementLabelHeldForZeroDollar, replacementLabelNotRequested } from './pendingLabels.js'

// The step checkboxes (2026-10-26, the agent draft panel): checked is the set
// of checked step indexes (a Set or an array); null or undefined means every
// step is checked (every other panel, and a draft as it loads).
const isChecked = (checked, i) => (checked == null ? true : (checked instanceof Set ? checked.has(i) : checked.includes(i)))

const stepName = (a, i) => `Step ${i + 1}${a?.label ? ` (${a.label})` : ''}`

// The steps Process all runs, and the ones that stop it:
//   - run: every processable step that has not succeeded yet, except a
//     customer-facing message step (the reply is the message; it would reach
//     the customer twice);
//   - blocked: a step that still needs the person first (an input to fill in,
//     or a return's "Check if scrap" / re-estimate). Process all is disabled
//     while any step is blocked, with the reasons as its tooltip;
//   - manual (2026-10-27): a console step that would carry out a promise (a
//     refund, replacement, return label or cancellation step with no API
//     call, @cxhub/shared's isManualStep()). CXHub cannot process it, so
//     Process all skips it and lists it ("Done in the console, not here
//     (skipped)"); the Send at the end just sends, no confirm and no block
//     (docs/decisions/2026-10-27-no-manual-step-confirm.md). Every such step CXHub runs carries its call
//     (server/src/ai/step-call-previews.js); other steps run as before.
// An unchecked step (2026-10-26) is neither run, nor blocking, nor manual.
// Send blocks follow the reply (2026-10-27, @cxhub/shared's step-blocks.js):
// with the reply given, a step that would block is blocking only while the
// reply depends on it; otherwise (the reply does not use it, or the person
// marked it "My reply doesn't need this", notNeeded) it is skipped: left out
// of the plan and listed in skipped with why. Without a reply, as before.
// A console step the reply does not depend on is skipped the same way (not
// needed for this reply); one it depends on is manual. A step is in at most one of blocked, skipped and manual.
// { indexes: [n], blocked: [{ index, reason }], skipped: [{ index, reason }],
//   manual?: [n] } (skipped only when the reply is given)
// replyCarrierIndex (2026-10-27, @cxhub/shared's replyCarrierStepIndex()):
// on the agent draft panel with the reply posted to an open eBay inquiry, the
// inquiry refund or shipment proof that carries the reply as its
// buyer-facing comment. It runs (its comment is the reply, so it is not
// skipped as a message step), and the reply is not sent again after it.
// A step that is the same step as an earlier one (2026-10-28, never the same
// step twice: the same type, params and call) is run once, in the earlier
// one's place.
export function processAllPlan({ actions = [], stepStates = {}, checkStates = {}, actionInputs = {}, checked = null, reply = undefined, signature = null, notNeeded = [], replyCarrierIndex = -1 } = {}) {
  const indexes = []
  const blocked = []
  const skipped = []
  const manual = []
  const repeats = duplicateStepIndexes(actions)
  // Whether the reply lifts the step (skipped, with why).
  const lifted = (i, a) => {
    if (reply === undefined) return false
    const state = stepBlockState(a, i, reply, { actions, signature, notNeeded })
    if (state.applies) return false
    skipped.push({ index: i, reason: state.reason })
    return true
  }
  const block = (i, a, reason) => {
    if (lifted(i, a)) return
    blocked.push({ index: i, reason })
  }
  ;(actions ?? []).forEach((a, i) => {
    if (!isProcessableStep(a)) return
    if (repeats.has(i)) return
    // A step started from Other Actions is processed in its own window
    // (2026-10-27, stepSlots.js), never by Process all.
    if (a?.source === 'manual_panel') return
    if (!isChecked(checked, i)) return
    // A replacement's label step while its $0 RMA box is checked
    // (2026-10-29): the label comes from the $0 RMA, never this step.
    if (replacementLabelHeldForZeroDollar(a, actions, actionInputs)) return
    // A $0 RMA replacement's own label step nobody checked (2026-10-30):
    // optional, never run by Process all.
    if (replacementLabelNotRequested(a, i, actions, actionInputs)) return
    if (stepProcessGate(a, stepStates?.[i]) === 'done') return
    if (i === replyCarrierIndex) { indexes.push(i); return }
    if (isCustomerFacingSendAction(a, actionInputs?.[i])) return
    if (isManualStep(a) && stepPromiseKinds(a.type).length) {
      if (!lifted(i, a)) manual.push(i)
      return
    }
    if (!isActionInputSatisfied(a, actionInputs?.[i])) {
      block(i, a, `${stepName(a, i)} needs its input filled in first.`)
      return
    }
    if (a.type === 'process_return_refund') {
      const cs = checkStates?.[i]
      // A check made stale by an edit of the items or reason code (2026-10-27)
      // waits for Check again, as a missing one does.
      if (cs?.status !== 'checked' || (cs.result?.rateQuotes && actionInputs?.[i]?.estimateStale) || checkIsStale(cs.result?.checkedItems ? cs.result : null, actionInputs?.[i])) {
        block(i, a, `${stepName(a, i)} needs its check first.`)
        return
      }
    }
    indexes.push(i)
  })
  const out = { indexes, blocked }
  if (reply !== undefined) out.skipped = skipped
  if (manual.length) out.manual = manual
  return out
}

// The indexes of steps that repeat an earlier step exactly (2026-10-28):
// the same type, params and call (method, URL, body).
export function duplicateStepIndexes(actions = []) {
  const seen = new Set()
  const out = new Set()
  ;(actions ?? []).forEach((a, i) => {
    if (!a?.type) return
    const id = JSON.stringify([a.type, a.params ?? null, a.apiCall?.method ?? null, a.apiCall?.url ?? null, a.apiCall?.body ?? null])
    if (seen.has(id)) out.add(i)
    else seen.add(id)
  })
  return out
}

// The inputs Process all sends: the panel's own, plus what a step already
// processed on its own hands to a later one (the same threading as the
// panel's per-step Process): the replacement's id and items for its return
// label. When both run in the same Process all, the server passes it on.
// replyCarrierIndex / reply (2026-10-27): the step that carries the reply
// as its comment gets the reply as its message (editedMessage).
export function processAllInputs({ actions = [], actionInputs = {}, stepStates = {}, indexes = [], replyCarrierIndex = -1, reply = null } = {}) {
  let inputs = { ...(actionInputs ?? {}) }
  if (replyCarrierIndex >= 0 && (indexes ?? []).includes(replyCarrierIndex) && String(reply ?? '').trim()) {
    inputs = { ...inputs, [replyCarrierIndex]: { ...(inputs[replyCarrierIndex] ?? {}), editedMessage: reply } }
  }
  for (const i of indexes ?? []) {
    const a = actions?.[i]
    const sib = replacementLabelSiblingIndex(a, actions)
    if (sib !== null && stepStates?.[sib]?.status === 'success' && stepStates[sib].result?.id) {
      inputs = { ...inputs, [i]: { ...(inputs[i] ?? {}), replacementId: stepStates[sib].result.id, items: actionInputs?.[sib]?.items } }
    }
    // address_confirmation_update_address reads a flat `address` (as the
    // per-step Process sends it).
    if (a?.type === 'address_confirmation_update_address' && inputs[i]?.editedAddress) {
      const { editedAddress, ...rest } = inputs[i]
      inputs = { ...inputs, [i]: { ...rest, address: editedAddress } }
    }
  }
  return inputs
}

// The panel's step states after a Process all: a step that ran is ✓ or ✗; a
// step the run never reached (it stopped at a failure) goes back to idle.
export function stepStatesAfterProcessAll(prev = {}, indexes = [], results = []) {
  const next = { ...(prev ?? {}) }
  const byIndex = new Map((results ?? []).map((r) => [r.index, r]))
  for (const i of indexes ?? []) {
    const r = byIndex.get(i)
    if (!r || r.skipped) next[i] = { status: 'idle', result: null, error: null }
    else if (r.success) next[i] = { status: 'success', result: r.result ?? null, error: null }
    // A refund stopped by an open eBay return or inquiry (2026-10-29): the
    // "Refund on the return / inquiry" button reads ebayRefundReroute.
    else next[i] = { status: 'failed', result: null, error: r.error ?? 'Action failed', ...(r.ebayRefundReroute ? { ebayRefundReroute: r.ebayRefundReroute } : {}), ...(r.partlyDone ? { partlyDone: true, stepState: r.stepState ?? null, checklist: r.checklist ?? [] } : {}) }
  }
  return next
}

// Whether the panel offers Process all: the reply promises a refund,
// replacement, return label or cancellation (@cxhub/shared's
// findPromisedActions()) and a step of a promised kind is still to run or
// blocked, or (2026-10-26) the reply keeps a label link slot that a "recover
// and send" step still to run fills. Any other contact keeps Send alone, as
// before.
// checked (2026-10-26): only a checked step counts (an unchecked fill step
// is not in the plan, so its slot offers nothing).
// anyStep (2026-10-27, the agent draft panel): offered whenever at least
// one checked step can be processed (to run, or waiting on the person),
// whatever the reply says. The pipeline layout leaves it off (unchanged).
export function offersProcessAll(actions = [], plan = { indexes: [], blocked: [] }, reply = '', checked = null, { anyStep = false } = {}) {
  if (anyStep) return processAllStepCount(plan, checked) > 0
  const fillIndex = labelLinkFillStepIndex(actions)
  if (fillIndex >= 0 && isChecked(checked, fillIndex) && String(reply ?? '').includes(LABEL_LINK_TOKEN) && (plan?.indexes ?? []).includes(fillIndex)) return true
  // An RMARTN Cust Pays reply (2026-10-27, rmaReference.js): its Reference ID
  // is the RMA the return step makes, so a reply that keeps the RMA token
  // offers Process all while that step is still to run (blocked or not, so
  // a Check the person still owes shows as the reason).
  const rmaIndex = rmaReferenceStepIndex(actions)
  if (rmaIndex >= 0 && isChecked(checked, rmaIndex) && hasRmaReferenceToken(reply) && [...(plan?.indexes ?? []), ...(plan?.blocked ?? []).map((b) => b.index)].includes(rmaIndex)) return true
  const promised = new Set(findPromisedActions(reply).map((p) => p.kind))
  if (!promised.size) return false
  return [...(plan?.indexes ?? []), ...(plan?.blocked ?? []).map((b) => b.index)].some((i) => isChecked(checked, i) && stepPromiseKinds(actions?.[i]?.type).some((k) => promised.has(k)))
}

// The checked steps Process all runs or waits on (plan.indexes plus the
// blocked ones), counted once.
export function processAllStepCount(plan = { indexes: [], blocked: [] }, checked = null) {
  const all = new Set([...(plan?.indexes ?? []), ...(plan?.blocked ?? []).map((b) => b.index)])
  return [...all].filter((i) => isChecked(checked, i)).length
}

// The one button's label on the agent draft panel and the shadow panel
// (2026-10-27): "Process 2 steps and send reply" with steps and a reply;
// "Process 2 steps" with steps and no reply (the case waits for the
// buyer); "Send Reply" with no steps.
export function processAllButtonLabel(count, { reply = true } = {}) {
  const n = Number(count) || 0
  if (n <= 0) return 'Send Reply'
  const steps = `Process ${n} ${n === 1 ? 'step' : 'steps'}`
  return reply ? `${steps} and send reply` : steps
}

// The offer, for the agent draft panel and the shadow preview:
// { offered, count, label }. offered: there is a checked step to process.
export function processAllOffer({ actions = [], plan = { indexes: [], blocked: [] }, reply = '', checked = null } = {}) {
  const count = processAllStepCount(plan, checked)
  const hasReply = Boolean(String(reply ?? '').trim())
  return { offered: offersProcessAll(actions, plan, reply, checked, { anyStep: true }), count, label: processAllButtonLabel(count, { reply: hasReply }) }
}

// "Step 2 (Create return label) failed: <error>. The reply was not sent."
export function processAllFailureMessage(failed) {
  if (!failed) return null
  const name = `Step ${Number(failed.index) + 1}${failed.label ? ` (${failed.label})` : ''}`
  // TRQ's message and hint may end with a period already (2026-10-27).
  return `${name} failed: ${String(failed.error ?? 'Action failed').replace(/\.\s*$/, '')}. The reply was not sent.`
}

// The confirmation's list: "1. Process replacement ..." for each step to run.
// checked (2026-10-26): an unchecked step is not listed.
export function processAllStepList(actions = [], indexes = [], checked = null) {
  return (indexes ?? []).filter((i) => isChecked(checked, i)).map((i) => `${i + 1}. ${actions?.[i]?.label ?? actions?.[i]?.type ?? 'Step'}`)
}

// The confirmation's skipped lines (2026-10-27): "Skipped: 3. Create the
// return (not needed for this reply)" for each step the reply lifted.
export function processAllSkippedList(actions = [], skipped = []) {
  return (skipped ?? []).map((s) => `Skipped: ${s.index + 1}. ${actions?.[s.index]?.label ?? actions?.[s.index]?.type ?? 'Step'} (${STEP_NOT_NEEDED_NOTE.toLowerCase()})`)
}

// --- The step checkboxes (2026-10-26) ------------------------------------------

// The steps that must run together: { dependent index: the index it needs
// first }. Only the dependencies the panel already knows (a later step that
// uses an earlier step's result, suggestionActionInputs.js's sibling
// helpers): the replacement's return label needs the replacement, a resent
// label's message needs the created label, an automated return notice's
// message needs its new label, and an eBay refund's note needs the refund.
// Since 2026-10-28 also an eBay cancel request's pairs (ebayCancelPairIndex()).
export function stepDependencies(actions = []) {
  const deps = {}
  const list = actions ?? []
  list.forEach((a, i) => {
    for (const find of [replacementLabelSiblingIndex, resendLabelCreateSiblingIndex, automatedReturnNoticeLabelSiblingIndex, issueEbayReturnRefundNoteSiblingIndex, ebayCancelPairIndex]) {
      const sib = find(a, list)
      if (sib !== null && sib !== undefined && sib !== i) { deps[i] = sib; return }
    }
  })
  return deps
}

// An eBay cancel request's steps run in pairs (2026-10-28,
// docs/decisions/2026-10-28-agent-ebay-cancel-two-steps.md): eBay's approve
// with the console cancel before it, and the reject's console note with the
// reject before it. They are checked and unchecked together, and the first
// runs first. A pipeline step names its request (params.ebayCancelId); an
// agent step preview names the step it runs with (runsWithKey). Returns the
// index of the step this one runs with, or null.
export function ebayCancelPairIndex(a, list = []) {
  const steps = list ?? []
  if (a?.runsWithKey) {
    const j = steps.findIndex((x) => x?.key === a.runsWithKey)
    return j >= 0 ? j : null
  }
  const id = a?.params?.ebayCancelId
  if (!id) return null
  const needs = a.type === 'approve_ebay_cancellation' ? 'cancel_order_trq' : (a.type === 'add_order_note' ? 'reject_ebay_cancellation' : null)
  if (!needs) return null
  const j = steps.findIndex((x) => x?.type === needs && x?.params?.ebayCancelId === id)
  return j >= 0 ? j : null
}

// The pairs that are checked and unchecked together: { index: partner },
// both ways.
export function stepPairs(actions = []) {
  const pairs = {}
  ;(actions ?? []).forEach((a, i) => {
    const j = ebayCancelPairIndex(a, actions)
    if (j !== null && j !== i) { pairs[i] = j; pairs[j] = i }
  })
  return pairs
}

// The checked set as indexes, from the unchecked list: [0..n) minus it.
export function checkedIndexes(actions = [], unchecked = []) {
  const off = new Set((unchecked ?? []).map(Number))
  return (actions ?? []).map((_, i) => i).filter((i) => !off.has(i))
}

// Toggling one step: unchecking a step also unchecks every step that needs
// it (they cannot run without it); checking a step also checks the step it
// needs. Returns the new unchecked list, sorted, and the other steps that
// changed with it ({ index, why }) for the tooltip.
export function toggleStepCheck({ actions = [], unchecked = [], index, checked }) {
  const deps = stepDependencies(actions)
  const off = new Set((unchecked ?? []).map(Number))
  const also = []
  if (checked) {
    off.delete(index)
    let need = deps[index]
    while (need !== undefined && off.has(need)) {
      off.delete(need)
      also.push({ index: need, why: 'needed' })
      need = deps[need]
    }
  } else {
    off.add(index)
    const queue = [index]
    while (queue.length) {
      const p = queue.shift()
      for (const [d, n] of Object.entries(deps)) {
        const di = Number(d)
        if (n === p && !off.has(di)) {
          off.add(di)
          also.push({ index: di, why: 'needs' })
          queue.push(di)
        }
      }
    }
  }
  // A pair goes together both ways (2026-10-28): checking the first also
  // checks the second, unchecking the second also unchecks the first.
  const pairs = stepPairs(actions)
  for (const i of [index, ...also.map((x) => x.index)]) {
    const partner = pairs[i]
    if (partner === undefined || partner === index || also.some((x) => x.index === partner)) continue
    if (checked && off.has(partner)) { off.delete(partner); also.push({ index: partner, why: 'together' }) }
    if (!checked && !off.has(partner)) { off.add(partner); also.push({ index: partner, why: 'together' }) }
  }
  return { unchecked: [...off].sort((x, y) => x - y), also }
}

// The checkbox's tooltip for a step with a dependency, or null: "Step 2
// needs step 1: unchecking step 1 also unchecks step 2." number(i) gives the
// number the panel shows for index i.
export function stepDependencyNote(actions = [], index, number = (i) => i + 1) {
  // A pair (2026-10-28): one note for both steps.
  const partner = stepPairs(actions)[index]
  if (partner !== undefined) {
    const [first, second] = [index, partner].sort((x, y) => x - y).map(number)
    return `Steps ${first} and ${second} run together: checking or unchecking one does the same to the other, and step ${first} runs first.`
  }
  const deps = stepDependencies(actions)
  const notes = []
  if (deps[index] !== undefined) notes.push(`This step needs step ${number(deps[index])} to run first: checking it also checks step ${number(deps[index])}, and unchecking step ${number(deps[index])} unchecks this one.`)
  const dependents = Object.entries(deps).filter(([, n]) => n === index).map(([d]) => number(Number(d)))
  if (dependents.length) notes.push(`Step ${dependents.join(', ')} ${dependents.length > 1 ? 'need' : 'needs'} this step: unchecking it also unchecks step ${dependents.join(', ')}.`)
  return notes.length ? notes.join(' ') : null
}

// The confirmation's note for the console steps it skips (2026-10-27).
export function processAllManualNote(actions = [], manual = []) {
  if (!manual?.length) return null
  const names = manual.map((i) => `${i + 1}. ${actions?.[i]?.label ?? actions?.[i]?.type ?? 'Step'}`)
  return `Done in the console, not here (skipped): ${names.join('; ')}`
}

// The label link slot (2026-10-26, @cxhub/shared's label-link-slot.js): the
// reply keeps INSERTLABELLINK where a label's link goes when the label could
// not be recovered while the suggestion was built; its "recover and send"
// step fills it at Process.

// The reply as the placeholder warnings for Process read it: a slot the step
// still fills is not a placeholder for the person (Send alone still refuses
// it until the step ran).
// plan (2026-10-26, the step checkboxes): when given, the slot is skipped
// only while its fill step is in this Process all's plan. An unchecked fill
// step never runs, so its slot stays a placeholder and Process all is
// refused like Send.
export function replyForProcessChecks(reply, actions = [], stepStates = {}, plan = null) {
  return labelSlotFilledByProcessAll(reply, actions, stepStates, plan) ? String(reply ?? '').split(LABEL_LINK_TOKEN).join('') : reply
}

// Whether Process all fills the reply's slot: the slot is pending and its
// fill step runs in this pass (plan omitted: the step still to run).
export function labelSlotFilledByProcessAll(reply, actions = [], stepStates = {}, plan = null) {
  if (!hasPendingLabelLinkSlot(reply, actions, stepStates)) return false
  return plan == null || (plan?.indexes ?? []).includes(labelLinkFillStepIndex(actions))
}

// The warning under Send while the slot is unfilled.
export const LABEL_LINK_SLOT_WARNING = 'The return label link goes where INSERTLABELLINK is: process the return label step (or use Process all steps and send reply) and it is filled in before the reply is sent.'

// The reply after a Process all: the slot filled from the fill step's result,
// when that step ran in this pass (one block per box when the labels are one
// return's boxes), and the eBay print line taken out when a step's label did
// not go onto the eBay return (2026-10-27). Unchanged otherwise.
// ran (the step checkboxes, 2026-10-26): { indexes, stepStates } of this pass.
// When given, only a step in indexes counts: an unchecked fill step fills no
// slot, and an unchecked label step that would attach or create a label
// (labelStepWritesEbayLabel()) and has not already succeeded attaches
// nothing, so the eBay print line comes out.
export function replyAfterProcessAll(reply, actions = [], results = [], ran = null) {
  const inPass = ran ? new Set((ran.indexes ?? []).map(Number)) : null
  const ok = (results ?? []).filter((x) => x?.success && (!inPass || inPass.has(Number(x.index))))
  let withoutLine = replyAfterEbayAttach(reply, ok)
  if (inPass && (actions ?? []).some((a, j) => labelStepWritesEbayLabel(a) && !inPass.has(j) && ran.stepStates?.[j]?.status !== 'success')) {
    withoutLine = withoutEbayPrintLine(withoutLine)
  }
  const i = labelLinkFillStepIndex(actions)
  if (i < 0) return withoutLine
  const r = ok.find((x) => x?.index === i)
  return r ? fillLabelLinkSlot(withoutLine, labelLinkSlotUrls(r.result), { boxes: r.result?.boxes === true }) : withoutLine
}

// Whether a step puts a return label on the buyer's eBay return at Process
// (2026-10-27): an attach step (params.attachToEbayReturn), or a label step
// that writes a label not already on the return (a create, or a recover
// whose label the grounding did not find attached).
const LABEL_STEP_TYPES = new Set(['resend_return_label', 'automated_return_notice_resend_label'])
export function labelStepWritesEbayLabel(a) {
  if (!a || !isProcessableStep(a)) return false
  if (a.params?.attachToEbayReturn === true) return true
  return LABEL_STEP_TYPES.has(a.type) && a.params?.attachedToEbayReturn !== true
}
